import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

// No pass/fail flag -- matches RMS's grade_scale_rows exactly (it has
// none either); pass/fail is derived server-side from the grade LETTER
// vs. the board's own failure grade (F / U), not stored per row.
export type GradeRow = {
  grade: string
  minPercentage: number
  maxPercentage: number
  colorCode?: string
  remarks?: string
}

// 2026-10-04 RMS-parity rewrite: a Grade Scale is global now (one document,
// created once per session) -- no longer tied to one campus. Where it
// applies lives separately in GradeConfigScope (see GradeConfigScope type
// below), same split as Term/TermScope.
// Scope summary as returned inline by the list endpoint (for the table's
// own "Scopes" column) -- a flattened, read-only view, not the full
// scope-row shape used by the Scopes modal (see GradeConfigScope below).
export type GradeConfigScopeSummary = {
  branchName: string
  branchCode?: string
  systemCode?: string
  systemName?: string
  classId?: string
  className?: string
}

export type GradeConfig = {
  _id: string
  name: string
  description?: string
  session: { _id: string; title: string } | string
  system: { _id: string; name: string } | string
  grades: GradeRow[]
  isActive: boolean
  createdAt?: string
  createdBy?: { firstName?: string; lastName?: string }
  scopes?: GradeConfigScopeSummary[]
}

export type GradeConfigScope = {
  _id?: string
  branch: string | { _id: string; name: string }
  systemId: string | { _id: string; name: string; code?: string }
  // Omitted/undefined means "all classes for this campus+board".
  classId?: string | { _id: string; name: string } | null
}

const BASE = "/grade-config"

export const listGradeConfigs = async (params?: Record<string, any>) => {
  const res = await apiClient({ method: "GET", url: appendQueryParams(BASE, params || {}) })
  // shape: { success, data: { configs, pagination } }
  return res.data
}

export const getGradeConfigById = async (id: string) => {
  const res = await apiClient({ method: "GET", url: `${BASE}/${id}` })
  return res.data
}

export const createGradeConfig = async (data: {
  name: string
  description?: string
  session: string
  system: string
  grades?: GradeRow[]
  isActive?: boolean
}) => {
  const res = await apiClient({ method: "POST", url: BASE, data })
  return res.data
}

export const updateGradeConfig = async (
  id: string,
  data: { name?: string; description?: string; session?: string; system?: string; grades?: GradeRow[]; isActive?: boolean }
) => {
  const res = await apiClient({ method: "PUT", url: `${BASE}/${id}`, data })
  return res.data
}

export const deleteGradeConfig = async (id: string) => {
  const res = await apiClient({ method: "DELETE", url: `${BASE}/${id}` })
  return res.data
}

// Which (campus, board, class) tuples this scale applies to.
export const getGradeConfigScopes = async (id: string): Promise<GradeConfigScope[]> => {
  const res = await apiClient({ method: "GET", url: `${BASE}/${id}/scopes` })
  return res.data.data
}

// Full replace -- same pattern as Term's own scopes save.
export const setGradeConfigScopes = async (
  id: string,
  scopes: { branch: string; systemId: string; classId?: string | null }[]
): Promise<GradeConfigScope[]> => {
  const res = await apiClient({ method: "PUT", url: `${BASE}/${id}/scopes`, data: { scopes } })
  return res.data.data
}
